/**
 * Thin client for the AIDIA backend.
 *
 * Everything this module sends is already encrypted by lib/crypto.js. The only
 * cleartext on the wire is routing metadata: which vault, which record id, what
 * kind of collection it belongs to, and when it changed.
 */

const BASE = "/api";

// Identifies this browser to the backend so Settings can show a device list.
const DEVICE_KEY = "aidia.deviceId";
export function deviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function deviceLabel() {
  const ua = navigator.userAgent;
  const os = /Mac/.test(ua) ? "Mac" : /Win/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "Device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "Browser";
  return `${os} · ${browser}`;
}

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message);
    this.status = status;
    this.body = body || {};
  }
}

async function request(path, { method = "GET", body, token, headers = {} } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        ...(body ? { "content-type": "application/json" } : {}),
        ...(token ? { "x-sync-token": token } : {}),
        "x-device-id": deviceId(),
        "x-device-label": deviceLabel(),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError(0, "Can't reach the AIDIA server. Is the backend running?");
  }

  const text = await res.text();
  const data = text ? safeParse(text) : {};

  if (!res.ok) {
    // A 5xx with a non-JSON body is almost always the dev proxy failing to
    // reach the API, not the API itself answering — say the useful thing.
    const unreachable = res.status >= 500 && data.error === undefined;
    throw new ApiError(
      unreachable ? 0 : res.status,
      unreachable
        ? "The AIDIA server isn't responding. Start it with: npm run dev:backend"
        : data.error || `Server error ${res.status}`,
      data
    );
  }
  return data;
}

const safeParse = (t) => {
  try {
    return JSON.parse(t);
  } catch {
    return {};
  }
};

// --- health ---------------------------------------------------------------
export const health = () => request("/health");

// --- vault identity -------------------------------------------------------
export const createVault = ({ syncId, salt, verifier, token, registrationToken }) =>
  request("/vaults", {
    method: "POST",
    body: { syncId, salt, verifier, token },
    // Only required on a server that has closed public registration.
    headers: registrationToken ? { "x-registration-token": registrationToken } : {},
  });

export const getVault = (syncId, token) =>
  request(`/vaults/${encodeURIComponent(syncId)}`, { token });

export const getVaultStatus = (syncId, token) =>
  request(`/vaults/${encodeURIComponent(syncId)}/status`, { token });

export const deleteVault = (syncId, token) =>
  request(`/vaults/${encodeURIComponent(syncId)}`, {
    method: "DELETE",
    token,
    headers: { "x-confirm-delete": "yes" },
  });

// --- encrypted records ----------------------------------------------------
export const pullRecords = (syncId, token, since = 0) =>
  request(`/vaults/${encodeURIComponent(syncId)}/records?since=${since}`, { token });

export const pushRecords = (syncId, token, records) =>
  request(`/vaults/${encodeURIComponent(syncId)}/records`, {
    method: "POST",
    token,
    body: { records },
  });

// --- AI -------------------------------------------------------------------
export const aiStatus = () => request("/ai/status");

export const aiMessage = ({ model, system, messages, maxTokens, effort }) =>
  request("/ai/messages", {
    method: "POST",
    body: { model, system, messages, maxTokens, effort },
  });
